import React, { useEffect } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { CompareArrows as CompareIcon } from "@mui/icons-material";
import { colors } from "../../theme";
import { marketingKeyframes, matchupVsBadge } from "./marketingStyles";
import MarketingHeadshot from "./MarketingHeadshot";
import {
  MARKETING_HEADSHOT_DISPLAY_SIZE,
  preloadMarketingHeadshots,
} from "../../utils/marketingHeadshots";
import { MARKETING_MATCHUPS } from "./marketingMatchupsConfig";

const MatchupPlayer = ({ playerId, name, era, accent }) => (
  <Box
    sx={{
      flex: 1,
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.75,
    }}
  >
    <MarketingHeadshot
      playerId={playerId}
      name={name}
      size={MARKETING_HEADSHOT_DISPLAY_SIZE}
      accent={accent}
    />
    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
      {name}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {era}
    </Typography>
  </Box>
);

const MatchupCard = ({ matchup, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      ...marketingKeyframes,
      p: 2.5,
      borderRadius: 2,
      border: `1px solid ${colors.border}`,
      bgcolor: "background.paper",
      cursor: "pointer",
      transition: "transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
      animation: `float 4s ease-in-out infinite`,
      animationDelay: matchup.delay,
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: matchup.accent,
        opacity: 0.8,
      },
      "&:hover": {
        transform: "translateY(-6px)",
        borderColor: matchup.accent,
        boxShadow: `0 12px 40px ${matchup.accent}22`,
      },
    }}
  >
    <Chip
      label={matchup.question}
      size="small"
      sx={{
        mb: 2,
        bgcolor: `${matchup.accent}18`,
        color: matchup.accent,
        fontWeight: 600,
        fontSize: "0.7rem",
        height: 24,
      }}
    />
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
      <MatchupPlayer
        playerId={matchup.playerIdA}
        name={matchup.playerA}
        era={matchup.eraA}
        accent={matchup.accent}
      />
      <Box sx={matchupVsBadge(matchup.accent)}>
        <CompareIcon />
      </Box>
      <MatchupPlayer
        playerId={matchup.playerIdB}
        name={matchup.playerB}
        era={matchup.eraB}
        accent={matchup.accent}
      />
    </Box>
  </Box>
);

const MatchupCards = ({ onCardClick }) => {
  useEffect(() => {
    preloadMarketingHeadshots();
  }, []);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 2,
      }}
    >
      {MARKETING_MATCHUPS.map((matchup, index) => (
        <MatchupCard
          key={matchup.question}
          matchup={matchup}
          onClick={() => onCardClick?.(matchup, index)}
        />
      ))}
    </Box>
  );
};

export default MatchupCards;
